import { create } from 'zustand';
import type { Aircraft, Airport, Bbox, CoverageCircle, Enrichment, Filters, FocusTrack, SourceHealth, WatchEntry } from '../types';
import { DEFAULT_FILTERS } from '../types';
import type { UserLocation } from '../geolocate';

export type ConnectionState = 'connecting' | 'connected' | 'reconnecting';

/**
 * How much position history to draw. Replaces the old boolean "full trail"
 * layer toggle: the same two states plus "off", so hiding trails no longer
 * means turning every other overlay off to find the map underneath.
 */
export type TrailMode = 'off' | 'short' | 'full';

export interface MapLayers {
  rings: boolean;
  routes: boolean;
  /** Observed traffic-density heatmap ("common flight lanes") from recent live positions — not official airway charts. */
  lanes: boolean;
  /** Boundary of the area currently being queried for live data — not physical receiver locations. */
  coverage: boolean;
  threeD: boolean;
  /** Predicted remaining-leg line (current position -> known destination) for every aircraft. */
  predicted: boolean;
  /**
   * Aviation "hobbyist" view: dark map tiles, streets/roads/buildings/POIs
   * hidden, and glide-slope + standard-approach overlays rendered for every
   * airport worldwide with published runway data — real ILS data (localizer
   * freq, named FAF) for a curated set of major US airports, a generic
   * assumed 3° path elsewhere. Persisted to localStorage.
   */
  hobbyist: boolean;
  /** Ground-based radar coverage rings at `radarAltitudeFt`. */
  showRadar: boolean;
  /**
   * Draw aircraft at their real size on the ground at every zoom (zoomed out
   * they shrink to small markers). Off: they stop shrinking at a readable
   * size that still keeps relative sizes. Persisted to localStorage.
   */
  trueScale: boolean;
  /** Iridium NEXT satellite positions + Aireon coverage footprints. */
  showSatellites: boolean;
  /** RainViewer precipitation radar overlay. */
  showPrecip: boolean;
  /** METAR flight-category station overlay. */
  showMetars: boolean;
}

interface AppState {
  area: Bbox | null;
  areaName: string | null;
  aircraft: Map<string, Aircraft>;
  airports: Airport[];
  sources: SourceHealth[];
  lastUpdateTs: number | null;
  connection: ConnectionState;
  selectedIcao24: string | null;
  selectedEnrichment: Enrichment | null;
  /** Airport ident whose arrivals/departures board is open, or null. */
  selectedAirport: string | null;
  followSelected: boolean;
  filters: Filters;
  layers: MapLayers;
  /** Trail history drawn per aircraft — see TrailMode. */
  trails: TrailMode;
  /** Target altitude (ft) used to compute radar-horizon coverage rings. */
  radarAltitudeFt: number;
  /** Persisted (localStorage) watched aircraft — see `findWatchEntry` in types.ts for matching. */
  watchlist: WatchEntry[];
  /** User-drawn coverage circle (Layers menu "Coverage circle" tool), or null if none is set — see MapView's circle-to-bbox wiring. */
  coverageCircle: CoverageCircle | null;
  /** True while the coverage-circle draw tool is armed (map awaiting mousedown/drag/mouseup to place a circle). */
  drawingCircle: boolean;
  /** Whole recorded path of the selected plane's flight, or of a past flight picked from its history. */
  focusTrack: FocusTrack | null;
  /** Height (px) of the phone bottom sheet covering the map, so the camera can keep the plane above it. 0 when none. */
  sheetInset: number;
  /** Where the user is, once they've shared their location (drawn as a dot on the map). */
  userLocation: UserLocation | null;

  setArea(area: Bbox, name?: string | null): void;
  applySnapshot(aircraft: Aircraft[], sources: SourceHealth[], ts: number): void;
  applyUpdate(aircraft: Aircraft[], removed: string[], sources: SourceHealth[], ts: number): void;
  setAirports(airports: Airport[]): void;
  setConnection(c: ConnectionState): void;
  select(icao24: string | null): void;
  selectAirport(ident: string | null): void;
  setSelectedEnrichment(e: Enrichment | null): void;
  setFollow(follow: boolean): void;
  setFilters(filters: Partial<Filters>): void;
  toggleLayer(layer: keyof MapLayers): void;
  /** Set several layers at once — for grouped controls where one switch drives more than one layer. */
  setLayers(layers: Partial<MapLayers>): void;
  setTrails(mode: TrailMode): void;
  setRadarAltitude(ft: number): void;
  addWatch(kind: WatchEntry['kind'], value: string): void;
  removeWatch(id: string): void;
  setCoverageCircle(circle: CoverageCircle | null): void;
  setDrawingCircle(drawing: boolean): void;
  setFocusTrack(track: FocusTrack | null): void;
  setSheetInset(px: number): void;
  setUserLocation(loc: UserLocation | null): void;
}

const TRAIL_MAX_AGE_MS = 15 * 60 * 1000;

/**
 * Max trail age to draw for a mode. "off" returns a negative age so every
 * segment is older than the bound and nothing is drawn — cheaper and simpler
 * than threading a second "draw trails at all" flag through the layer code.
 */
export function trailMaxAge(mode: TrailMode): number {
  return mode === 'full' ? Infinity : mode === 'off' ? -1 : TRAIL_MAX_AGE_MS;
}
// "Full trail" retention cap — matches the server's trail retention so the
// client holds at most what the server can ever send, rather than growing
// unbounded for the life of the session.
const FULL_TRAIL_MAX_AGE_MS = 3 * 60 * 60 * 1000;

/**
 * Read the persisted hobbyist toggle from localStorage so the very first
 * map render already knows the right base style. Avoids a one-frame
 * LIBERTY flash when the user had hobbyist mode on and refreshes.
 */
function initialHobbyist(): boolean {
  try {
    return localStorage.getItem('planesight:hobbyist') === '1';
  } catch {
    /* private mode / SSR-ish context */
    return false;
  }
}

/** Persisted "true size" switch; on unless the user turned it off. */
function initialTrueScale(): boolean {
  try {
    return localStorage.getItem('planesight:trueScale') !== '0';
  } catch {
    return true;
  }
}

/** Read the persisted watchlist from localStorage at store init (same idiom as `initialHobbyist`). */
function initialWatchlist(): WatchEntry[] {
  try {
    const raw = localStorage.getItem('planesight:watchlist');
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveWatchlist(list: WatchEntry[]): void {
  try {
    localStorage.setItem('planesight:watchlist', JSON.stringify(list));
  } catch {
    /* private mode / storage full */
  }
}

export const useStore = create<AppState>((set, get) => ({
  area: null,
  areaName: null,
  aircraft: new Map(),
  airports: [],
  sources: [],
  lastUpdateTs: null,
  connection: 'connecting',
  selectedIcao24: null,
  selectedEnrichment: null,
  selectedAirport: null,
  followSelected: false,
  filters: DEFAULT_FILTERS,
  layers: {
    rings: false,
    routes: false,
    lanes: false,
    coverage: false,
    threeD: false,
    predicted: false,
    hobbyist: initialHobbyist(),
    trueScale: initialTrueScale(),
    showRadar: false,
    showSatellites: false,
    showPrecip: false,
    showMetars: false,
  },
  trails: 'short',
  radarAltitudeFt: 10000,
  watchlist: initialWatchlist(),
  coverageCircle: null,
  drawingCircle: false,
  focusTrack: null,
  sheetInset: 0,
  userLocation: null,

  setArea(area, name = null) {
    set({
      area,
      areaName: name,
      aircraft: new Map(),
      selectedIcao24: null,
      selectedEnrichment: null,
      selectedAirport: null,
      focusTrack: null,
    });
  },

  applySnapshot(aircraft, sources, ts) {
    const map = new Map<string, Aircraft>();
    for (const ac of aircraft) map.set(ac.icao24, ac);
    set({ aircraft: map, sources, lastUpdateTs: ts });
  },

  applyUpdate(aircraft, removed, sources, ts) {
    const prev = get().aircraft;
    // Only clone the Map when we actually mutate it. Most updates touch a
    // subset of aircraft, so shallow-copying on demand avoids the cost of
    // re-creating a Map with hundreds/thousands of entries every 2 s.
    let map = prev;
    const ensureMutable = () => {
      if (map === prev) map = new Map(prev);
      return map;
    };
    // "Full trail" keeps a long history (capped at the server's retention)
    // instead of the short rolling window used normally.
    // Retention is independent of what's drawn: "off" still accumulates the
    // short window, so switching trails back on shows history immediately
    // instead of starting from an empty line.
    const cutoff = Date.now() - (get().trails === 'full' ? FULL_TRAIL_MAX_AGE_MS : TRAIL_MAX_AGE_MS);
    for (const ac of aircraft) {
      const existing = prev.get(ac.icao24);
      if (existing) {
        // Updates carry only the newest trail point — append to local history.
        const trail = [...existing.trail];
        const incoming = ac.trail[ac.trail.length - 1];
        const last = trail[trail.length - 1];
        if (incoming && (!last || incoming.ts > last.ts)) trail.push(incoming);
        while (trail.length && trail[0].ts < cutoff) trail.shift();
        const next = { ...ac, trail };
        // Skip the set() entirely when nothing meaningful changed, so
        // subscribers keyed off `s.aircraft !== prev.aircraft` don't see
        // spurious churn for aircraft that didn't actually move.
        if (
          next.lat !== existing.lat ||
          next.lon !== existing.lon ||
          next.altitudeFt !== existing.altitudeFt ||
          next.groundSpeedKt !== existing.groundSpeedKt ||
          next.track !== existing.track ||
          next.verticalRateFpm !== existing.verticalRateFpm ||
          next.callsign !== existing.callsign ||
          next.squawk !== existing.squawk ||
          next.onGround !== existing.onGround ||
          next.lastSeen !== existing.lastSeen ||
          next.trail.length !== existing.trail.length ||
          next.trail[next.trail.length - 1]?.ts !== existing.trail[existing.trail.length - 1]?.ts
        ) {
          ensureMutable().set(ac.icao24, next);
        }
      } else {
        ensureMutable().set(ac.icao24, ac);
      }
    }
    for (const icao of removed) {
      if (prev.has(icao)) ensureMutable().delete(icao);
    }
    let selectedIcao24 = get().selectedIcao24;
    let selectedEnrichment = get().selectedEnrichment;
    let followSelected = get().followSelected;
    if (selectedIcao24 && !map.has(selectedIcao24)) {
      selectedIcao24 = null;
      selectedEnrichment = null;
      followSelected = false;
    }
    // The selected plane's full track keeps growing from the live feed.
    let focusTrack = get().focusTrack;
    if (focusTrack?.live) {
      const sel = selectedIcao24 ? map.get(selectedIcao24) : undefined;
      const p = sel?.trail[sel.trail.length - 1];
      const last = focusTrack.points[focusTrack.points.length - 1];
      if (!sel || sel.icao24 !== focusTrack.flight.icao24) {
        focusTrack = null;
      } else if (p && (!last || p.ts > last.ts)) {
        focusTrack = {
          ...focusTrack,
          points: [...focusTrack.points, { ...p, groundSpeedKt: sel.groundSpeedKt, track: sel.track, onGround: sel.onGround }],
        };
      }
    }
    set({ aircraft: map, sources, lastUpdateTs: ts, selectedIcao24, selectedEnrichment, followSelected, focusTrack });
  },

  setAirports(airports) {
    set({ airports });
  },

  setConnection(connection) {
    set({ connection });
  },

  select(selectedIcao24) {
    const prev = get().selectedIcao24;
    set({
      selectedIcao24,
      selectedEnrichment: selectedIcao24 === prev ? get().selectedEnrichment : null,
      followSelected: selectedIcao24 ? get().followSelected : false,
      // The flight panel and the airport board share the same screen slot.
      selectedAirport: selectedIcao24 ? null : get().selectedAirport,
      focusTrack: selectedIcao24 === prev ? get().focusTrack : null,
    });
  },

  selectAirport(selectedAirport) {
    set({
      selectedAirport,
      ...(selectedAirport
        ? { selectedIcao24: null, selectedEnrichment: null, followSelected: false, focusTrack: null }
        : {}),
    });
  },

  setSelectedEnrichment(selectedEnrichment) {
    set({ selectedEnrichment });
  },

  setFollow(followSelected) {
    set({ followSelected });
  },

  setFilters(filters) {
    set({ filters: { ...get().filters, ...filters } });
  },

  toggleLayer(layer) {
    const layers = get().layers;
    set({ layers: { ...layers, [layer]: !layers[layer] } });
  },

  setLayers(layers) {
    set({ layers: { ...get().layers, ...layers } });
  },

  setTrails(trails) {
    set({ trails });
  },

  setRadarAltitude(radarAltitudeFt) {
    set({ radarAltitudeFt });
  },

  addWatch(kind, value) {
    const trimmed = value.trim();
    if (!trimmed) return;
    const normalized = kind === 'icao24' ? trimmed.toLowerCase() : trimmed.toUpperCase();
    const id = `${kind}:${normalized}`;
    const existing = get().watchlist;
    if (existing.some((w) => w.id === id)) return;
    const watchlist = [...existing, { id, kind, value: normalized }];
    set({ watchlist });
    saveWatchlist(watchlist);
  },

  removeWatch(id) {
    const watchlist = get().watchlist.filter((w) => w.id !== id);
    set({ watchlist });
    saveWatchlist(watchlist);
  },

  setCoverageCircle(coverageCircle) {
    set({ coverageCircle });
  },

  setDrawingCircle(drawingCircle) {
    set({ drawingCircle });
  },

  setFocusTrack(focusTrack) {
    set({ focusTrack });
  },

  setSheetInset(sheetInset) {
    if (sheetInset !== get().sheetInset) set({ sheetInset });
  },

  setUserLocation(userLocation) {
    set({ userLocation });
  },
}));
